import { DateTime } from 'luxon'
import { version } from '../../../'
import { default as YAML } from 'yaml'
import { default as pMap } from 'p-map'
import { Middleware } from '@nhtio/middleware'
import { encode, decode } from '../../encoding'
import { negotiateContent } from '../utils/http'
import { validator } from '@nhtio/lucid-resourceful/joi'
import { isObject, isResourcefulModel } from '../../type_guards'
import { resolveModelDisplayColumn } from '../utils/relationships'
import { E_MISSING_CURRENT_INSTANCE_ID } from '@nhtio/lucid-resourceful/errors'
import { ResourcefulRouterModelResolverService } from './resourceful_router_model_resolver_service'
import type { NextFn } from '@nhtio/middleware'
import type { HttpContext } from '@adonisjs/core/http'
import type { Schema } from '@nhtio/lucid-resourceful/joi'
import type { ApplicationService } from '@adonisjs/core/types'
import type { ResourcefulModel } from '@nhtio/lucid-resourceful/types'
import type { DatabaseQueryBuilderContract } from '@adonisjs/lucid/types/querybuilder'
import type { ResourcefulRouterOptionsService } from './resourceful_router_options_service'
import type { ResourcefulModelController } from '../controllers/resourceful_model_controller'

const encoder = (_key: string, value: any) => {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toISO()
  }
  if (value instanceof DateTime) {
    return value.toISO()
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  return value
}

const makeEncodable = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map((item) => makeEncodable(item))
  }
  if (data instanceof Date) {
    return DateTime.fromJSDate(data).toISO()
  }
  if (data instanceof DateTime) {
    return data.toISO()
  }
  if (isObject(data)) {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, makeEncodable(value)])
    )
  }
  return data
}

type ResourcefulRouterChoicesServiceMiddlewareContext = {
  ctx: HttpContext
  app: ApplicationService
  query: DatabaseQueryBuilderContract
}

type ResourcefulRouterChoicesServiceMiddlewareFn = (
  ctx: ResourcefulRouterChoicesServiceMiddlewareContext,
  next: NextFn
) => void | Promise<void>

type ResourcefulRouterChoicesRequestAttributes = {
  uid: string | number | bigint
  modelValue: string | number | bigint
  search: string
  exclude: Array<string | number | bigint>
}

export class ResourcefulRouterChoicesService {
  readonly #options: ResourcefulRouterOptionsService
  // @ts-ignore
  readonly #models: ResourcefulRouterModelResolverService
  // @ts-ignore
  readonly #controllers: Map<string, ResourcefulModelController>

  constructor(
    options: ResourcefulRouterOptionsService,
    models: ResourcefulRouterModelResolverService,
    controllers: Map<string, ResourcefulModelController>
  ) {
    this.#options = options
    this.#models = models
    this.#controllers = controllers
  }

  async #resolveRequestRequirements(ctx: HttpContext) {
    const { default: app } = await import('@adonisjs/core/services/app')
    const serveAs = negotiateContent(ctx)
    const method = ctx.request.method() || 'GET'
    const url = ctx.request.url() || '/'
    const respond = (status: number, body: any, headers: { [key: string]: string } = {}) => {
      ctx.response.status(status)
      // apply macro-level headers
      Object.entries(this.#options.headers).forEach(([key, value]) => {
        ctx.response.header(key, value)
      })
      // apply request-level headers
      Object.entries(headers).forEach(([key, value]) => {
        ctx.response.header(key, value)
      })
      // if response is requested as encoded, return as encoded plaintext
      if (ctx.request.header('x-resourceful-response-encoded') === '1') {
        ctx.response.header(
          'Content-Type',
          `application/vnd.resourceful.structured; coding="zlib+base64"; ver="${version}"`
        )
        const encodable = makeEncodable(body)
        ctx.response.send(encode(encodable))
        return
      }
      // apply content-type headers
      switch (serveAs) {
        case 'yaml':
          ctx.response.header('Content-Type', 'application/x-yaml')
          break
        default:
          ctx.response.header('Content-Type', 'application/json')
          break
      }
      if ('undefined' !== typeof body && 204 !== status) {
        switch (serveAs) {
          case 'yaml':
            ctx.response.send(YAML.stringify(body, encoder))
            break
          default:
            ctx.response.send(JSON.stringify(body, encoder))
            break
        }
      }
    }
    return {
      app,
      serveAs,
      method,
      url,
      respond,
    }
  }

  async #resolveModel(modelName: string): Promise<ResourcefulModel | undefined> {
    const resolvedModelEntries = await pMap(
      Object.getOwnPropertyNames(this.#models),
      async (name) => {
        const modelOrError = await this.#models[name]
        if (!isResourcefulModel(modelOrError)) {
          return [name, undefined] as const
        }
        return [modelOrError.name, modelOrError] as const
      },
      { concurrency: 5 }
    )
    const resolvedModels = Object.fromEntries(resolvedModelEntries)
    return resolvedModels[modelName]
  }

  #extractValidatedRequestAttribute<A extends keyof ResourcefulRouterChoicesRequestAttributes>(
    attribute: A,
    schema: Schema<any>,
    ctx: HttpContext
  ): ResourcefulRouterChoicesRequestAttributes[A] | undefined {
    const original = ctx.request.input(attribute)
    if ('undefined' === typeof original || null === original) {
      return undefined
    }
    const { error, value } = schema.validate(original, { abortEarly: true, convert: true })
    if (error) {
      return undefined
    }
    return value as ResourcefulRouterChoicesRequestAttributes[A]
  }

  #getValidatedValue<A>(value: unknown, schema: Schema<any>, fallback: A): A {
    const { error, value: validated } = schema.validate(value, { abortEarly: true, convert: true })
    if (error) {
      return fallback
    }
    return validated as A
  }

  async handleRequest(ctx: HttpContext) {
    const { respond, app } = await this.#resolveRequestRequirements(ctx)
    /*
     |--------------------------------------------------------------------------
     | Decode model and relationship information from the URL
     |--------------------------------------------------------------------------
     */
    const doubleEncoded = Array.isArray(ctx.params['*']) ? ctx.params['*'][0] : ctx.params['*']
    if ('string' !== typeof doubleEncoded) {
      return respond(400, [])
    }
    const encoded = decodeURIComponent(doubleEncoded)
    let decoded: any = {}
    try {
      decoded = decode(encoded)
    } catch {
      return respond(400, [])
    }
    const { ResourcefulModel, relationship } = decoded as {
      ResourcefulModel: string
      relationship: string
    }
    /*
     |--------------------------------------------------------------------------
     | Resolve the model from the name of the model class
     |--------------------------------------------------------------------------
     */
    const model = await this.#resolveModel(ResourcefulModel)
    if (!model) {
      return respond(404, [])
    }
    /*
     |--------------------------------------------------------------------------
     | Resolve the relationship from the model
     |--------------------------------------------------------------------------
     */
    const resourcefulRelationship = model.$resourcefulRelationships.get(relationship)
    if (!resourcefulRelationship) {
      return respond(404, [])
    }
    const relatedModel = resourcefulRelationship.relatedModel()
    if (!isResourcefulModel(relatedModel)) {
      return respond(404, [])
    }
    /*
     |--------------------------------------------------------------------------
     | Check the ACL for reading the related model from the rules on the
     | base model's relationship definition
     |--------------------------------------------------------------------------
     */
    const canReadFilterResults = await pMap(
      resourcefulRelationship.readAccessControlFilters,
      async (filter) => filter(ctx, app),
      { concurrency: 5 }
    )
    if (canReadFilterResults.includes(false)) {
      return respond(403, [])
    }
    /*
     |--------------------------------------------------------------------------
     | Enqueue all scope hooks for the related model
     |--------------------------------------------------------------------------
     */
    const middleware = new Middleware<ResourcefulRouterChoicesServiceMiddlewareFn>()
    /*
     |--------------------------------------------------------------------------
     | Initialize the query so that we can apply scopes
     |--------------------------------------------------------------------------
     */
    const valueColumn = relatedModel.primaryKey
    const displayColumn = resolveModelDisplayColumn(relatedModel)
    /*
     |--------------------------------------------------------------------------
     | Not used in MVP
     |--------------------------------------------------------------------------
     | This functionality will aide in pagination in the future, but it's not
     | needed for the MVP
     */
    // @ts-ignore
    const modelValue = this.#extractValidatedRequestAttribute(
      'modelValue',
      validator.alternatives(validator.string(), validator.number(), validator.bigint()),
      ctx
    )
    // @ts-ignore
    const search = this.#extractValidatedRequestAttribute('search', validator.string().trim(), ctx)
    // @ts-ignore
    const exclude = this.#extractValidatedRequestAttribute(
      'exclude',
      validator
        .array()
        .items(validator.alternatives(validator.string(), validator.number(), validator.bigint())),
      ctx
    )

    const connectionClient = relatedModel.$adapter.modelConstructorClient(relatedModel)
    const queryTable = relatedModel.table
    const query: DatabaseQueryBuilderContract = connectionClient.query().from(queryTable)
    if (Array.isArray(this.#options.scopeRestrictors)) {
      this.#options.scopeRestrictors.forEach((restrictor) => {
        middleware.add(async (_, next) => {
          await restrictor(ctx, app, query, relatedModel)
          await next()
        })
      })
    }
    if (Array.isArray(relatedModel.$resourcefulQueryScopeCallbacks.list)) {
      relatedModel.$resourcefulQueryScopeCallbacks.list.forEach((scopeCallback) => {
        middleware.add(async (_, next) => {
          await scopeCallback(ctx, app, query, relatedModel)
          await next()
        })
      })
    }
    if (model.table === relatedModel.table) {
      /*
      |--------------------------------------------------------------------------
      | Self-referential relationships
      |--------------------------------------------------------------------------
      | For self-referential relationships, we require the current model's 
      | primary key value as a query parameter to avoid returning the base
      | model itself in the choices.
      */
      const currentModelInstanceId = this.#extractValidatedRequestAttribute(
        'uid',
        validator.alternatives(validator.string(), validator.number(), validator.bigint()),
        ctx
      )
      if (!currentModelInstanceId) {
        throw new E_MISSING_CURRENT_INSTANCE_ID()
      }
      middleware.add(async (_, next) => {
        // currentInstanceModelId.toString() is used to cast to a string to handle
        // bigint values which are not natively supported by the query builder
        query.whereNot(`${queryTable}.${valueColumn}`, currentModelInstanceId.toString())
        await next()
      })
    }
    await middleware.runner().run(async (fn, next) => fn({ ctx, app, query }, next))
    const countQuery = query.clone().count(`${valueColumn} as total`)
    /*
     |--------------------------------------------------------------------------
     | Dev Note
     |--------------------------------------------------------------------------
     | In the MVP we will return all of the results without any additional
     | filtration since the front-end will be handling that for now, and we
     | are not likely to have an issue until we hit 10,000+ results.
     |
     | In the future we will add a proper pagination mechanism and have it
     | coordinate with the front end using the ignored `modelValue`, `search`,
     | and `exclude` parameters.
     |--------------------------------------------------------------------------
     |
     | The `total` count will be eventually used as part of the pagination
     | functionality, and in order to maintain the contract that the `choices`
     | will be an attribute of the returned payload instead of the entire
     | returned payload, and because we already have the count query separated out
     | we are leaving it in during the MVP
     */
    const resultsQuery = query
      .clone()
      .select(`${valueColumn} as value`, `${displayColumn} as title`)
    const [countResults, choices] = await Promise.all([countQuery.first(), resultsQuery])
    const total = this.#getValidatedValue<number>(countResults?.total, validator.number().min(0), 0)
    respond(200, {
      total,
      choices,
    })
  }

  public static async serve(
    ctx: HttpContext,
    options: ResourcefulRouterOptionsService,
    models: ResourcefulRouterModelResolverService,
    controllers: Map<string, ResourcefulModelController>
  ) {
    const instance = new ResourcefulRouterChoicesService(options, models, controllers)
    return await instance.handleRequest(ctx)
  }
}
